import clsx from "clsx";
import type {
  DetailsHTMLAttributes,
  ElementType,
  HTMLAttributes,
  PropsWithChildren,
} from "react";

type SurfaceTone = "default" | "highlight" | "negative";

export function FrostedCard({
  children,
  className,
  tone = "default",
  as: Component = "div",
  ...props
}: PropsWithChildren<
  HTMLAttributes<HTMLElement> & {
    tone?: SurfaceTone;
    as?: ElementType;
  }
>) {
  return (
    <Component
      className={clsx(
        "relative overflow-hidden rounded-xl border p-6 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur-sm before:absolute before:inset-x-6 before:top-0 before:h-px before:bg-white/80 before:content-['']",
        tone === "default" && "border-white/70 bg-white/90",
        tone === "highlight" && "border-blue-500 bg-blue-50/90",
        tone === "negative" && "border-red-200 bg-white/90",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

export function DisclosureCard({
  children,
  className,
  ...props
}: PropsWithChildren<DetailsHTMLAttributes<HTMLDetailsElement>>) {
  return (
    <details
      className={clsx(
        "rounded-2xl border border-slate-200 bg-white/80 p-5",
        className
      )}
      {...props}
    >
      {children}
    </details>
  );
}

export function NestedDisclosureCard({
  children,
  className,
  ...props
}: PropsWithChildren<DetailsHTMLAttributes<HTMLDetailsElement>>) {
  return (
    <details
      className={clsx(
        "rounded-xl border border-slate-200 bg-white px-4 py-3",
        className
      )}
      {...props}
    >
      {children}
    </details>
  );
}

export function ToolPanel({
  children,
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLElement>>) {
  return (
    <section
      className={clsx(
        "rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-[0_18px_55px_rgba(15,23,42,0.10)] backdrop-blur sm:p-8",
        className
      )}
      {...props}
    >
      {children}
    </section>
  );
}

export function InsetPanel({
  children,
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) {
  return (
    <div
      className={clsx(
        "rounded-2xl border border-slate-200 bg-slate-50 p-4",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function ControlCluster({
  children,
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) {
  return (
    <div
      className={clsx(
        "inline-flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}
